import Box from '@mui/material/Box';
import { useCallback } from 'react';
import { US_REGION_CODE } from '../../../../constants.ts';
import { audienceLocation } from '../../constants.ts';
import { DemographicsSection } from '../expandableSections/demographics/DemographicsSection.tsx';
import { LocationSection } from '../expandableSections/location/LocationSection.tsx';
import { LookalikeSegmentsSection } from '../expandableSections/lookalike/LookalikeSegmentsSection.tsx';
import { MarketSection } from '../expandableSections/market/MarketSection.tsx';
import { SegmentsSection } from '../expandableSections/segments/SegmentsSection.tsx';
import type { GroupContentBaseProps } from './groupContentTypes.ts';

export type GroupContentRegionFirstProps = GroupContentBaseProps & {
  selectedRegionCode: string | null;
};

export const GroupContentRegionFirst = ({
  field,
  groupFieldName,
  groupName,
  index,
  isEdit,
  isEmptyGroup,
  listType,
  setDrawerState,
  update,
  isExpanded,
  toggle,
  setExpanded,
  selectedLocationSegment,
  handleSelectedLocationSegmentChange,
  isIdentityGraphMatchType,
  shouldDisplayLookalikeSection,
  selectedRegionCode,
  isCreate,
}: GroupContentRegionFirstProps) => {
  const hasRegionSelected = !!selectedRegionCode;
  const isUSRegion = selectedRegionCode === US_REGION_CODE;
  const isMarketSelectionLocked = !isCreate && !isEdit;
  const showSegmentsAndRulesSections = hasRegionSelected;
  const showLookalikeDemographicsLocation = hasRegionSelected && isUSRegion && isIdentityGraphMatchType;

  const collapseCriteriaSections = useCallback(() => {
    const uuid = field.uuid;

    setExpanded(`${uuid}_segments`, false);
    setExpanded(`${uuid}_lookalike`, false);
    setExpanded(`${uuid}_demographics`, false);
    setExpanded(`${uuid}_location`, false);
  }, [field.uuid, setExpanded]);

  return (
    <Box data-test="includes-segment-subgroup">
      <MarketSection
        groupFieldName={groupFieldName}
        index={index}
        onToggleExpand={toggle(`${field.uuid}_market`)}
        onCollapseCriteriaSections={collapseCriteriaSections}
        update={update}
        isExpanded={isExpanded(`${field.uuid}_market`)}
        isReadOnly={isMarketSelectionLocked}
      />

      {showSegmentsAndRulesSections && (
        <>
          <SegmentsSection
            isEdit={isEdit}
            field={field}
            isEmptyGroup={isEmptyGroup}
            groupName={groupName}
            groupFieldName={groupFieldName}
            listType={listType}
            setDrawerState={setDrawerState}
            update={update}
            index={index}
            isExpanded={isExpanded(`${field.uuid}_segments`)}
            onToggleExpand={toggle(`${field.uuid}_segments`)}
          />

          {showLookalikeDemographicsLocation && (
            <>
              {shouldDisplayLookalikeSection && (
                <LookalikeSegmentsSection
                  isEdit={isEdit}
                  field={field}
                  isEmptyGroup={isEmptyGroup}
                  groupName={groupName}
                  groupFieldName={groupFieldName}
                  listType={listType}
                  setDrawerState={setDrawerState}
                  update={update}
                  index={index}
                  isExpanded={isExpanded(`${field.uuid}_lookalike`)}
                  onToggleExpand={toggle(`${field.uuid}_lookalike`)}
                />
              )}

              <DemographicsSection
                isEdit={isEdit}
                groupFieldName={groupFieldName}
                listType={listType}
                update={update}
                index={index}
                isExpanded={isExpanded(`${field.uuid}_demographics`)}
                onToggleExpand={toggle(`${field.uuid}_demographics`)}
              />

              <LocationSection
                isEdit={isEdit}
                groupFieldName={groupFieldName}
                listType={listType}
                update={update}
                index={index}
                isExpanded={isExpanded(`${field.uuid}_location`)}
                onToggleExpand={toggle(`${field.uuid}_location`)}
                selectedLocationSegment={selectedLocationSegment ?? audienceLocation.Any}
                setSelectedLocationSegment={segment => handleSelectedLocationSegmentChange(segment, index)}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};
